<div class="journal-form">
  @if (Loading) {
  <p class="muted" role="status">Loading journal…</p>
  } @else {
  <div class="status-row">
    <span class="status-badge" [attr.data-status]="IsDeleted ? 'Deleted' : Status">{{ IsDeleted ? 'In recycle bin' : Status }}</span>
    @if (Entry) {
    <span class="muted">{{ Entry.JournalNumber }} · Period {{ Entry.PeriodName }} ({{ Entry.PeriodStatus }}) · Prepared by {{ Entry.PreparedByName || 'unknown' }}</span>
    }
  </div>

  @if (IsDeleted) {
  <p class="notice" role="status">Deleted by {{ Entry.DeletedByName || 'unknown' }} on {{ Entry.DeletedDate | date:'MM/dd/yyyy h:mm a' }}: "{{ Entry.DeleteReason }}". Retention: indefinite (kept permanently). Restoring returns it to Draft with the same number; it is never posted automatically.</p>
  }
  @if (PeriodClosed && (IsNew || Status === 'Draft') && !IsDeleted) {
  <p class="notice" role="status">Period {{ DatePeriod?.PeriodName }} is closed. You can save this draft, but it can't be submitted until the period is reopened.</p>
  }
  @if (!CanPrepare) {
  <p class="muted" role="status">You can view this journal. Changing it requires Accounting preparer access.</p>
  }
  @if (Status === 'Canceled') {
  <p class="notice" role="status">Canceled by {{ Entry.CanceledByName || 'unknown' }}: "{{ Entry.CancelReason }}". This journal has no ledger impact.</p>
  }
  @if (Status === 'Posted') {
  <p class="notice posted" role="status">Approved and posted by {{ Entry.PostedByName || 'unknown' }} on {{ Entry.PostedDate | date:'MM/dd/yyyy h:mm a' }}. Posted journals can't be edited or deleted; correct them with a reversal.</p>
  }
  @if (Entry && (Entry.ReversesJournalEntryID || Entry.ReversedByJournalEntryID || Entry.ReplacesJournalEntryID || Entry.ReplacedByJournalEntryID)) {
  <div class="links">
    @if (Entry.ReversesJournalEntryID) { <button mat-button (click)="openJournal(Entry.ReversesJournalEntryID)">Reverses {{ Entry.ReversesJournalNumber }}</button> }
    @if (Entry.ReversedByJournalEntryID) { <button mat-button (click)="openJournal(Entry.ReversedByJournalEntryID)">Reversed by {{ Entry.ReversedByJournalNumber }} ({{ Entry.ReversedByStatus }})</button> }
    @if (Entry.ReplacesJournalEntryID) { <button mat-button (click)="openJournal(Entry.ReplacesJournalEntryID)">Replaces {{ Entry.ReplacesJournalNumber }}</button> }
    @if (Entry.ReplacedByJournalEntryID) { <button mat-button (click)="openJournal(Entry.ReplacedByJournalEntryID)">Replaced by {{ Entry.ReplacedByJournalNumber }} ({{ Entry.ReplacedByStatus }})</button> }
  </div>
  }

  <fieldset class="rms-fieldset">
    <legend>Journal</legend>
    <div class="header-grid">
      <rms-date name="EntryDate" label="Posting Date" [required]="true" [disabled]="!Editable" [(ngModel)]="Form.EntryDate"></rms-date>
      <div class="field"><label>Currency</label><div class="static">USD</div></div>
      <rms-input name="Reason" label="Reason" [required]="true" [disabled]="!Editable" [(ngModel)]="Form.Reason"></rms-input>
      <rms-input name="SourceReference" label="Source Reference" [required]="true" [disabled]="!Editable" placeholder="e.g. Bank statement Sep 2026" [(ngModel)]="Form.SourceReference"></rms-input>
      <rms-input name="SourceDocumentID" label="Source Document ID" [disabled]="!Editable" placeholder="Optional, must be unique" [(ngModel)]="Form.SourceDocumentID"></rms-input>
      <rms-input name="ExternalReference" label="External Reference" [disabled]="!Editable" placeholder="Optional, e.g. bank or processor reference" [(ngModel)]="Form.ExternalReference"></rms-input>
      <rms-select name="PaymentMethodID" [label]="MethodLabel" [disabled]="!Editable" [options]="PaymentMethodOptions" [(ngModel)]="Form.PaymentMethodID"></rms-select>
      @if (IsCheck) {
      <rms-input name="CheckNumber" label="Check #" [disabled]="!Editable" [(ngModel)]="Form.CheckNumber"></rms-input>
      }
      <rms-input name="Description" label="Description" [disabled]="!Editable" [(ngModel)]="Form.Description"></rms-input>
    </div>
  </fieldset>

  <fieldset class="rms-fieldset">
    <legend>Lines</legend>
    <table class="lines-table">
      <thead>
        <tr>
          <th class="num">#</th>
          <th class="account">Account</th>
          <th>Description</th>
          <th class="amount">Debit</th>
          <th class="amount">Credit</th>
          @if (Status === 'Posted') { <th class="ledger">Ledger row</th> }
          @if (Editable) { <th class="remove"></th> }
        </tr>
      </thead>
      <tbody>
        @for (line of Lines; track $index) {
        <tr>
          <td class="num">{{ $index + 1 }}</td>
          <td class="account">
            @if (Editable) {
            <rms-select [name]="'Account' + $index" label="Account" [includeSearch]="true" [options]="AccountOptions" [(ngModel)]="line.AccountID"></rms-select>
            } @else { {{ accountLabel(line.AccountID) }} }
          </td>
          <td>
            @if (Editable) {
            <input class="cell-input" [name]="'Description' + $index" [(ngModel)]="line.Description" aria-label="Line description" />
            } @else { {{ line.Description || '—' }} }
          </td>
          <td class="amount">
            @if (Editable) {
            <input class="cell-input amount-input" type="number" min="0" step="0.01" [name]="'Debit' + $index" [(ngModel)]="line.DebitAmount" (ngModelChange)="amountChanged(line, 'debit')" aria-label="Debit" />
            } @else { {{ line.DebitAmount ? (line.DebitAmount | currency:'USD') : '—' }} }
          </td>
          <td class="amount">
            @if (Editable) {
            <input class="cell-input amount-input" type="number" min="0" step="0.01" [name]="'Credit' + $index" [(ngModel)]="line.CreditAmount" (ngModelChange)="amountChanged(line, 'credit')" aria-label="Credit" />
            } @else { {{ line.CreditAmount ? (line.CreditAmount | currency:'USD') : '—' }} }
          </td>
          @if (Status === 'Posted') {
          <td class="ledger">{{ line.LedgerTransactionID ? '#' + line.LedgerTransactionID : (line.IsCashAccount ? 'Cash account (not written)' : '—') }}</td>
          }
          @if (Editable) {
          <td class="remove">
            <button mat-icon-button aria-label="Remove line" [disabled]="Lines.length <= 1" (click)="removeLine($index)"><mat-icon>delete_outline</mat-icon></button>
          </td>
          }
        </tr>
        }
      </tbody>
    </table>
    @if (Editable) {
    <button mat-button class="btn-bg-blue add-line" (click)="addLine()"><mat-icon>add</mat-icon> Add line</button>
    }

    <div class="totals">
      <div class="total-card"><span>Debit total</span><strong>{{ DebitTotal | currency:'USD' }}</strong></div>
      <div class="total-card"><span>Credit total</span><strong>{{ CreditTotal | currency:'USD' }}</strong></div>
      <div class="total-card" [class.off]="Difference !== 0"><span>Difference</span><strong>{{ Difference | currency:'USD' }}</strong></div>
      <div class="total-card"><span>Review</span><strong>{{ Status === 'Posted' ? 'Approved by ' + (Entry?.ReviewedByName || 'unknown') : Status === 'In review' ? 'Waiting for approval' : 'Required before posting' }}</strong></div>
    </div>
  </fieldset>

  <div class="actions">
    @if (IsDeleted) {
    @if (CanPrepare) {
    <button mat-button class="btn-bg-green" [disabled]="Busy" (click)="restore()">Restore to draft</button>
    }
    } @else if (Editable) {
    <button mat-button class="btn-bg-blue" [disabled]="Busy" (click)="save()">Save draft</button>
    <button mat-button class="btn-bg-green" [disabled]="Busy || !ReadyToSubmit" (click)="submit()">Submit for review</button>
    @if (!IsNew) {
    <button mat-button class="btn-bg-yellow" [disabled]="Busy" (click)="cancelJournal()">Cancel journal</button>
    <button mat-button class="btn-bg-red" [disabled]="Busy" (click)="deleteJournal()">Delete</button>
    }
    } @else if (Status === 'In review') {
    <button mat-button class="btn-bg-green" [disabled]="Busy || !!PostBlockedReason" [title]="PostBlockedReason" (click)="approveAndPost()">Approve &amp; post</button>
    @if (CanPrepare) {
    <button mat-button class="btn-bg-yellow" [disabled]="Busy" (click)="returnToDraft()">Return to preparer</button>
    <button mat-button class="btn-bg-red" [disabled]="Busy" (click)="cancelJournal()">Cancel journal</button>
    }
    } @else if (CanReverse) {
    <button mat-button class="btn-bg-yellow" [disabled]="Busy" (click)="reverse(false)">Reverse</button>
    <button mat-button class="btn-bg-yellow" [disabled]="Busy" (click)="reverse(true)">Reverse &amp; replace</button>
    }
    <span class="spacer"></span>
    <button mat-button (click)="close()">Close</button>
  </div>
  @if (Editable && !ReadyToSubmit && !PeriodClosed) {
  <p class="muted hint">Submit needs at least two lines with debits equal to credits. Drafts can be saved at any time.</p>
  }
  @if (Status === 'In review' && PostBlockedReason) {
  <p class="muted hint">{{ PostBlockedReason }}</p>
  }

  @if (History.length) {
  <fieldset class="rms-fieldset">
    <legend>History</legend>
    <ul class="history">
      @for (h of History; track h.JournalActionHistoryID) {
      <li>
        <span class="when">{{ h.ActionDate | date:'MM/dd/yyyy h:mm a' }}</span>
        <strong>{{ h.ActionType }}</strong>
        @if (h.FieldName && h.BeforeValue) { <span>{{ h.FieldName }}: {{ h.BeforeValue }} → {{ h.AfterValue }}</span> }
        @else if (h.ActionDetail) { <span>{{ h.ActionDetail }}</span> }
        @if (h.Reason) { <span class="reason">"{{ h.Reason }}"</span> }
        <span class="who">by {{ h.ActionByName || 'unknown' }}</span>
      </li>
      }
    </ul>
  </fieldset>
  }
  }
</div>
